import { useState } from "react";

import UpcomingTalks from "content-ui/talks/components/upcomingTalks/upcomingTalks";
import CommonHero from "components/commonHero/commonHero";
import ChipsFilter from "components/chipsFilter/chipsFilter";
import TextButton from "components/textButton/TextButtonLegacy";
import Button from "components/buttonNew/button";
import { pastTalks, upcomingTalks } from "./utils";
import type { TalksPageData } from "./types";
import { PastTalkCard } from "./components/pastTalkCard";
import Image from "components/imageNew/image";

const TalksView = ({ data }: { data: TalksPageData }) => {
  const NUMBER_OF_TALKS_PER_BLOCK = 8;

  const [loadMorePresses, setLoadMorePresses] = useState<number>(1);

  const { talksData } = data;
  const [activeFilters, setActiveFilters] = useState<string[] | []>([]);
  const handleFilterUpdate = async (filter: string[] | []) => {
    setActiveFilters(filter);
  };

  const allPastTalks = pastTalks(talksData);

  const buildCategories = () => {
    const markets: string[] = [];
    const categories: string[] = [];

    allPastTalks.forEach((talk) => {
      if (talk.marketLabel) {
        markets.push(talk.marketLabel);
      }
    });
    const uniqueCategories = Array.from(new Set(markets));

    uniqueCategories.forEach((marketLabel) => {
      categories.push(marketLabel);
    });

    return categories;
  };

  const uniqueCategories = buildCategories();

  const filteredPastTalks =
    activeFilters?.length > 0
      ? // @ts-ignore
        allPastTalks.filter((talk) => activeFilters.includes(talk.marketLabel))
      : allPastTalks;

  const displayedPastTalks = filteredPastTalks.slice(
    0,
    NUMBER_OF_TALKS_PER_BLOCK * loadMorePresses
  );

  const NoUpcomingTalks = () => {
    return (
      <div className="mb-[60px] lg:mb-[90px] lg:bg-[rgba(170,170,178,0.1)] lg:p-4">
        <div className="mx-auto flex max-w-[740px] flex-col items-center gap-y-[15px] text-center">
          <p className="font-bold text-[18px] text-black-rock">
            {data.noTalksHeading}
          </p>
          <p className="text-[16px] text-mono-40">{data.noTalksSubheading}</p>
          {data.noTalksImage?.url ? (
            <div className="h-[120px] w-[120px]">
              <Image
                src={data.noTalksImage.url}
                width={data.noTalksImage.width}
                height={data.noTalksImage.height}
                alt={data.noTalksImage.alt || ""}
                blurDataURL={data.noTalksImage.blurUpThumb}
              />
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <main className="w-100 overflow-x-hidden bg-mono-05 pt-[60px] text-black-rock lg:pt-[69px]">
      <div className="mb-[70px]">
        <CommonHero
          title={
            <span>
              <span className="text-cerise">~/ </span>
              {data.title}
            </span>
          }
          brandColor={"cerise"}
          subtitle={data.subtitle}
        />
      </div>
      <div className="mx-auto max-w-[1040px] px-5 pb-[100px] lg:w-full lg:max-w-[1220px] lg:px-0">
        <div data-testid={"upcoming-talks"}>
          {upcomingTalks(talksData).length === 0 ? (
            <NoUpcomingTalks />
          ) : (
            <UpcomingTalks
              upcomingTalks={upcomingTalks(talksData)}
              heading={data.upcomingHeading}
            />
          )}
        </div>
        <div className="mb-[22px]">
          <h2 className="font-bold text-[18px]">{data.pastHeading}</h2>
          <div className="mb-[12px] mt-[25px] flex items-center justify-between">
            <h4 className={"inline-flex font-bold text-[16px]"}>Filter</h4>
            <div className={"flex"}>
              <TextButton
                colour={"black"}
                component={"button"}
                onClick={() => {
                  handleFilterUpdate([]);
                  setLoadMorePresses(1);
                }}
              >
                Reset
              </TextButton>
            </div>
          </div>
          {allPastTalks.length > 0 && (
            <ChipsFilter
              type={"multiple"}
              brandingColor="grey"
              selected={activeFilters || []}
              list={uniqueCategories.map((marketLabel) => ({
                label: marketLabel,
                value: marketLabel,
              }))}
              onChange={async (selected: string[]) =>
                await handleFilterUpdate(selected)
              }
            />
          )}
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {displayedPastTalks.map((talk) => {
            return <PastTalkCard key={`past-${talk.id}`} {...talk} />;
          })}
        </div>
        <div className="flex w-full justify-center">
          {displayedPastTalks.length < filteredPastTalks.length ? (
            <Button
              className="mt-[50px]"
              onClick={() => setLoadMorePresses(loadMorePresses + 1)}
              size="small"
              buttonType="secondary"
              colour="black"
              aria-label="Load more competitors"
            >
              Load more
            </Button>
          ) : null}
        </div>
      </div>
    </main>
  );
};
export default TalksView;
